import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  type DateRange,
  DayPicker,
  type Matcher,
  getDefaultClassNames,
} from 'react-day-picker';
import 'react-day-picker/style.css';
import { useTranslation } from 'react-i18next';

import { Button } from '@fly/core';
import { Popover } from '@radix-ui/themes';

const formatDate = (d: Date | undefined) =>
  d
    ? d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '—';

const DAY_PICKER_STYLE = {
  '--rdp-day-height': '24px',
  '--rdp-day-width': '24px',
  '--rdp-day_button-height': '24px',
  '--rdp-day_button-width': '24px',
  '--rdp-accent-color': 'var(--teal-9)',
  '--rdp-accent-background-color': 'var(--slate-5)',
  '--rdp-range_middle-background-color': 'var(--slate-5)',
  '--rdp-range_middle-color': 'inherit',
  '--rdp-range_start-color': 'inherit',
  '--rdp-range_end-color': 'inherit',
  '--rdp-range_start-background':
    'linear-gradient(to right, transparent 50%, var(--slate-5) 50%)',
  '--rdp-range_end-background':
    'linear-gradient(to left, transparent 50%, var(--slate-5) 50%)',
  '--rdp-selected-border': 'none',
  '--rdp-today-color': 'inherit',
  '--rdp-nav_button-height': '20px',
  '--rdp-nav_button-width': '20px',
  '--rdp-nav-height': '20px',
  fontSize: '12px',
} as React.CSSProperties;

function buildClassNames() {
  const d = getDefaultClassNames();
  const n = 'font-normal! text-[12px]!';
  return {
    selected: `${d.selected} ${n}`,
    range_start: `${d.range_start} ${n}`,
    range_end: `${d.range_end} ${n}`,
    range_middle: `${d.range_middle} ${n}`,
    today: `${d.today} ${n}`,
    month_caption: `${d.month_caption} text-[14px]!`,
    chevron: `${d.chevron} fill-slate-5! hover:fill-mauveA-12!`,
  };
}

const formatMonthCaption = (date: Date) =>
  date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

export interface FlyDatePickerRangeProps {
  range: true;
  value: DateRange | undefined;
  onChange: (range: DateRange | undefined) => void;
}

export interface FlyDatePickerSingleProps {
  range?: false;
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
}

type FlyDatePickerProps = (
  | FlyDatePickerRangeProps
  | FlyDatePickerSingleProps
) & {
  inline?: boolean;
  startMonth?: Date;
  endMonth?: Date;
  maxSelectedDays?: number;
  'data-testid'?: string;
};

export function FlyDatePicker(props: FlyDatePickerProps) {
  const {
    range,
    inline,
    startMonth,
    endMonth,
    maxSelectedDays,
    'data-testid': testId,
  } = props;
  const { t } = useTranslation();
  const [popoverOpen, setPopoverOpen] = useState(false);

  const { today, defaultStartMonth } = useMemo(() => {
    const now = new Date();
    const yearAgo = new Date();
    yearAgo.setFullYear(yearAgo.getFullYear() - 1);
    return { today: now, defaultStartMonth: yearAgo };
  }, []);

  const effectiveStartMonth = startMonth ?? defaultStartMonth;
  const effectiveEndMonth = endMonth ?? today;

  const classNames = useMemo(buildClassNames, []);

  if (range) {
    return (
      <RangePicker
        value={props.value as DateRange | undefined}
        onChange={props.onChange as (range: DateRange | undefined) => void}
        startMonth={effectiveStartMonth}
        endMonth={effectiveEndMonth}
        today={today}
        popoverOpen={popoverOpen}
        setPopoverOpen={setPopoverOpen}
        classNames={classNames}
        t={t}
        testId={testId}
        inline={inline}
        maxSelectedDays={maxSelectedDays}
      />
    );
  }

  return (
    <SinglePicker
      value={props.value as Date | undefined}
      onChange={props.onChange as (date: Date | undefined) => void}
      startMonth={effectiveStartMonth}
      endMonth={effectiveEndMonth}
      today={today}
      popoverOpen={popoverOpen}
      setPopoverOpen={setPopoverOpen}
      testId={testId}
      classNames={classNames}
    />
  );
}

interface RangePickerInternalProps {
  value: DateRange | undefined;
  onChange: (range: DateRange | undefined) => void;
  startMonth: Date;
  endMonth: Date;
  today: Date;
  popoverOpen: boolean;
  setPopoverOpen: (open: boolean) => void;
  classNames: ReturnType<typeof buildClassNames>;
  t: (key: string, opts?: { defaultValue: string }) => string;
  testId?: string;
  inline?: boolean;
  maxSelectedDays?: number;
}

function RangePickerBody({
  draft,
  onSelect,
  onApply,
  onToday,
  startMonth,
  endMonth,
  classNames,
  t,
  testId,
  maxSelectedDays,
}: {
  draft: DateRange;
  onSelect: (range: DateRange | undefined, triggerDate: Date) => void;
  onApply: () => void;
  onToday: () => void;
  startMonth: Date;
  endMonth: Date;
  classNames: ReturnType<typeof buildClassNames>;
  t: (key: string, opts?: { defaultValue: string }) => string;
  testId?: string;
  maxSelectedDays?: number;
}) {
  const disabled = useMemo<Matcher[]>(() => {
    const matchers: Matcher[] = [{ before: startMonth, after: endMonth }];
    if (maxSelectedDays && draft.from && !draft.to) {
      const earliest = new Date(draft.from);
      earliest.setDate(earliest.getDate() - (maxSelectedDays - 1));
      const latest = new Date(draft.from);
      latest.setDate(latest.getDate() + (maxSelectedDays - 1));
      if (earliest > startMonth) matchers.push({ before: earliest });
      if (latest < endMonth) matchers.push({ after: latest });
    }
    return matchers;
  }, [startMonth, endMonth, maxSelectedDays, draft.from, draft.to]);

  return (
    <div data-testid={testId} className='flex flex-col gap-fly-3'>
      <DayPicker
        mode='range'
        selected={draft}
        onSelect={onSelect}
        numberOfMonths={2}
        startMonth={startMonth}
        endMonth={endMonth}
        formatters={{ formatMonthCaption }}
        disabled={disabled}
        excludeDisabled
        style={DAY_PICKER_STYLE}
        classNames={classNames}
      />
      <div className='border-slate-5 flex items-center justify-between border-t pt-fly-3'>
        <Button variant='soft' color='neutral' size='1' onClick={onToday}>
          {t('app.common.date_picker.today', {
            defaultValue: 'Today',
          })}
        </Button>
        <Button
          variant='solid'
          size='1'
          disabled={!draft.from || !draft.to}
          onClick={onApply}
        >
          {t('app.common.date_picker.apply', {
            defaultValue: 'Apply',
          })}
        </Button>
      </div>
    </div>
  );
}

function RangePicker({
  value,
  onChange,
  startMonth,
  endMonth,
  today,
  popoverOpen,
  setPopoverOpen,
  classNames,
  t,
  testId,
  inline,
  maxSelectedDays,
}: RangePickerInternalProps) {
  const [draft, setDraft] = useState<DateRange>({
    from: value?.from,
    to: value?.to,
  });
  const isNewSelection = useRef(true);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setPopoverOpen(open);
      if (open) {
        setDraft({ from: value?.from, to: value?.to });
        isNewSelection.current = true;
      }
    },
    [setPopoverOpen, value]
  );

  const handleSelect = useCallback(
    (range: DateRange | undefined, triggerDate: Date) => {
      if (isNewSelection.current) {
        isNewSelection.current = false;
        setDraft({ from: triggerDate, to: undefined });
        return;
      }
      const resolved = range ?? { from: triggerDate, to: undefined };
      if (maxSelectedDays && resolved.from && resolved.to) {
        const diffDays =
          Math.round(
            Math.abs(resolved.to.getTime() - resolved.from.getTime()) /
              86_400_000
          ) + 1;
        if (diffDays > maxSelectedDays) return;
      }
      setDraft(resolved);
    },
    [maxSelectedDays]
  );

  const handleApply = useCallback(() => {
    if (draft.from && draft.to) {
      onChange(draft);
    }
    setPopoverOpen(false);
  }, [draft, onChange, setPopoverOpen]);

  const handleToday = useCallback(() => {
    onChange({ from: today, to: today });
    setPopoverOpen(false);
  }, [today, onChange, setPopoverOpen]);

  if (inline) {
    return (
      <RangePickerBody
        draft={draft}
        onSelect={handleSelect}
        onApply={handleApply}
        onToday={handleToday}
        startMonth={startMonth}
        endMonth={endMonth}
        classNames={classNames}
        t={t}
        testId={testId}
        maxSelectedDays={maxSelectedDays}
      />
    );
  }

  return (
    <Popover.Root open={popoverOpen} onOpenChange={handleOpenChange}>
      <Popover.Trigger>
        <div
          data-testid={testId ?? 'fly-date-picker-trigger'}
          className='border-slate-4 bg-slateA-3 rounded-lg! flex cursor-pointer items-center'
        >
          <div className='text-slate-12 font-semibold flex items-center gap-fly-2 px-fly-2 py-fly-2 text-size-fly-1'>
            {formatDate(value?.from)}
          </div>
          -
          <div className='text-slate-12 font-semibold flex items-center gap-fly-2 px-fly-2 py-fly-2 text-size-fly-1'>
            {formatDate(value?.to)}
          </div>
        </div>
      </Popover.Trigger>
      <Popover.Content
        align='end'
        sideOffset={4}
        style={{ maxWidth: 'none', top: '5px' }}
      >
        <RangePickerBody
          draft={draft}
          onSelect={handleSelect}
          onApply={handleApply}
          onToday={handleToday}
          startMonth={startMonth}
          endMonth={endMonth}
          classNames={classNames}
          t={t}
          maxSelectedDays={maxSelectedDays}
        />
      </Popover.Content>
    </Popover.Root>
  );
}

interface SinglePickerInternalProps {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  startMonth: Date;
  endMonth: Date;
  today: Date;
  popoverOpen: boolean;
  setPopoverOpen: (open: boolean) => void;
  classNames: ReturnType<typeof buildClassNames>;
  testId?: string;
}

function SinglePicker({
  value,
  onChange,
  startMonth,
  endMonth,
  today,
  popoverOpen,
  setPopoverOpen,
  classNames,
  testId,
}: SinglePickerInternalProps) {
  const effectiveValue = value ?? today;

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setPopoverOpen(open);
    },
    [setPopoverOpen]
  );

  const handleSelect = useCallback(
    (date: Date | undefined) => {
      onChange(date);
      setPopoverOpen(false);
    },
    [onChange, setPopoverOpen]
  );

  return (
    <Popover.Root open={popoverOpen} onOpenChange={handleOpenChange}>
      <Popover.Trigger>
        <div
          data-testid={testId ?? 'fly-date-picker-trigger'}
          className='border-slate-4 bg-slateA-3 rounded-lg! flex cursor-pointer items-center'
        >
          <div className='text-slate-12 font-semibold flex items-center gap-fly-2 px-fly-2 py-fly-2 text-size-fly-1'>
            {formatDate(effectiveValue)}
          </div>
        </div>
      </Popover.Trigger>
      <Popover.Content
        align='end'
        sideOffset={4}
        style={{ maxWidth: 'none', top: '5px' }}
      >
        <DayPicker
          mode='single'
          selected={effectiveValue}
          onSelect={handleSelect}
          startMonth={startMonth}
          endMonth={endMonth}
          formatters={{ formatMonthCaption }}
          disabled={{ before: startMonth, after: endMonth }}
          style={DAY_PICKER_STYLE}
          classNames={classNames}
        />
      </Popover.Content>
    </Popover.Root>
  );
}
